import { CloseOutline18Icon } from "@vygruppen/spor-icon-react";
import {
  filterConditions,
  FilterData,
} from "features/History/Filter/FilterData";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { Dropdown } from "shared/components/forms/Dropdown";
import {
  DropsButton,
  FilterChip,
  Flex,
  HStack,
  MenuCheckboxItem,
  MenuItemGroup,
  MenuRadioItem,
  MenuRadioItemGroup,
} from "shared/components/Spor";
import { VerticalSeparator } from "shared/components/VerticalSeparators";

type FilterDropdownsProps = {
  filter: FilterData;
  setFilter: (filter: FilterData) => void;
};

export const FilterDropdowns: FC<FilterDropdownsProps> = ({
  filter,
  setFilter,
}) => {
  const { t } = useTranslation();

  const selectedAllConditions =
    filterConditions.length > 0 &&
    filter.condition.length === filterConditions.length;

  return (
    <HStack gap={2}>
      <div className="DontShowIfSmallWidth">
        <VerticalSeparator />
      </div>
      <Dropdown
        label={t("Vis skjulte")}
        selectedAmount={[filter.plannedEvents, filter.groupTravel].reduce(
          (sum, current) => (current ? sum + 1 : sum),
          0,
        )}
      >
        <MenuItemGroup>
          <MenuCheckboxItem
            value="planned-events"
            checked={filter.plannedEvents}
            onCheckedChange={() =>
              setFilter(filter.updatePlannedEvent(!filter.plannedEvents))
            }
          >
            {t("Planlagte hendelser")}
          </MenuCheckboxItem>
          <MenuCheckboxItem
            value="group-travel"
            checked={filter.groupTravel}
            onCheckedChange={() =>
              setFilter(filter.updateGroupTravel(!filter.groupTravel))
            }
          >
            {t("Gruppereise/assistanse")}
          </MenuCheckboxItem>
        </MenuItemGroup>
      </Dropdown>

      <VerticalSeparator />
      <Dropdown label={t("Tilstand")} selectedAmount={filter.condition.length}>
        <MenuItemGroup>
          <MenuCheckboxItem
            value="toggle-all-conditions"
            checked={selectedAllConditions}
            onCheckedChange={() =>
              setFilter(
                new FilterData({
                  ...filter,
                  condition: selectedAllConditions ? [] : [...filterConditions],
                }),
              )
            }
          >
            {t("Marker alle")}
          </MenuCheckboxItem>
          {filterConditions.map((condition) => (
            <MenuCheckboxItem
              key={condition}
              value={condition}
              checked={filter.condition.includes(condition)}
              onCheckedChange={() =>
                setFilter(filter.updateCondition(condition))
              }
            >
              {t(condition)}
            </MenuCheckboxItem>
          ))}
        </MenuItemGroup>
      </Dropdown>

      <Dropdown
        label={t("Synlighet")}
        selectedAmount={filter.visibility.length}
      >
        <MenuItemGroup>
          {(["Synlig", "Utgått"] as const).map((visibility) => (
            <MenuCheckboxItem
              key={visibility}
              value={visibility}
              checked={filter.visibility.includes(visibility)}
              onCheckedChange={() =>
                setFilter(filter.updateVisibility(visibility))
              }
            >
              {t(visibility)}
            </MenuCheckboxItem>
          ))}
        </MenuItemGroup>
      </Dropdown>

      <Dropdown
        label={t("Togserie")}
        selectedAmount={filter.trainSeries.length}
      >
        <Flex
          gap="6px"
          flexWrap="wrap"
          p="2px"
          minWidth="80px"
          maxWidth="250px"
          minHeight="30px"
        >
          {filter.allTrainIds.map((trainId) => (
            <FilterChip
              key={trainId}
              size="xs"
              checked={filter.trainSeries.includes(trainId)}
              onCheckedChange={() => setFilter(filter.updateTrainIds(trainId))}
            >
              {trainId}
            </FilterChip>
          ))}
        </Flex>
      </Dropdown>

      <Dropdown
        label={`${t("Land")}: ${
          filter.countryCode === "NO" ? t("Norge") : t("Sverige")
        }`}
      >
        <MenuItemGroup>
          <MenuRadioItemGroup value={filter.countryCode}>
            <MenuRadioItem
              cursor="pointer"
              value="NO"
              onClick={() => setFilter(filter.updateCountryCode("NO"))}
            >
              {t("Norge")}
            </MenuRadioItem>
            <MenuRadioItem
              cursor="pointer"
              value="SE"
              onClick={() => setFilter(filter.updateCountryCode("SE"))}
            >
              {t("Sverige")}
            </MenuRadioItem>
          </MenuRadioItemGroup>
        </MenuItemGroup>
      </Dropdown>

      {!filter.isEmpty() && (
        <DropsButton
          variant="ghost"
          size="xs"
          width="112px"
          fontWeight="normal"
          onClick={() => setFilter(new FilterData())}
          leftIcon={<CloseOutline18Icon />}
        >
          {t("Fjern filter")}
        </DropsButton>
      )}
    </HStack>
  );
};
